import { useEffect, useState } from "react";
import { adminApi, type RuleRow } from "../lib/admin-api";

export default function RulesEditor() {
  const [rules, setRules] = useState<RuleRow[]>([]);
  const [search, setSearch] = useState("");
  const [carrier, setCarrier] = useState("");
  const [category, setCategory] = useState("");
  const [dueForReview, setDueForReview] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [dueCount, setDueCount] = useState(0);

  async function refresh() {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (carrier) params.carrier = carrier;
    if (category) params.category = category;
    if (dueForReview) params.dueForReview = "1";
    const res = await adminApi.listRules(params);
    setRules(res.rules);
    setDueCount(res.dueForReviewCount);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, carrier, category, dueForReview]);

  return (
    <div className="stack">
      <h2>Postal rules</h2>
      {dueCount > 0 && (
        <div className="banner banner-warn">
          <span>{dueCount} rule{dueCount === 1 ? "" : "s"} require review (never verified or older than 6 months).</span>
        </div>
      )}

      <div className="card" style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ flex: 2, minWidth: 160, minHeight: 44, padding: "0 12px", borderRadius: 8, border: "1px solid var(--color-border)" }} />
        <input placeholder="Carrier (e.g. royal_mail)" value={carrier} onChange={(e) => setCarrier(e.target.value)} style={{ flex: 1, minWidth: 140, minHeight: 44, padding: "0 12px", borderRadius: 8, border: "1px solid var(--color-border)" }} />
        <input placeholder="Category" value={category} onChange={(e) => setCategory(e.target.value)} style={{ flex: 1, minWidth: 140, minHeight: 44, padding: "0 12px", borderRadius: 8, border: "1px solid var(--color-border)" }} />
        <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)" }}>
          <input type="checkbox" checked={dueForReview} onChange={(e) => setDueForReview(e.target.checked)} />
          Due for review
        </label>
      </div>

      <div className="stack-sm">
        {rules.map((r) => (
          <div className="card stack-sm" key={r.id}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-3)" }}>
              <div>
                <strong>{r.name}</strong>
                <div className="field-help">
                  {r.category} {r.carrier ? `· ${r.carrier}` : ""} {r.service ? `· ${r.service}` : ""} {r.postal_mode ? `· ${r.postal_mode}` : ""}
                </div>
              </div>
              <span className="tag" style={{ background: effectColor(r.effect) }}>{r.effect}</span>
            </div>
            <div className="field-help">
              {r.status === "DISABLED" && <span style={{ color: "var(--color-danger-text)" }}>DISABLED · </span>}
              Last verified: {r.last_verified_at ? new Date(r.last_verified_at).toLocaleDateString("en-GB") : "Never"}
              {r.source_name && (
                <>
                  {" · Source: "}
                  <a href={r.source_url ?? "#"} target="_blank" rel="noopener noreferrer">
                    {r.source_name}
                  </a>
                </>
              )}
            </div>

            {expanded === r.id ? (
              <RuleForm
                rule={r}
                onSaved={async () => {
                  setExpanded(null);
                  await refresh();
                }}
                onCancel={() => setExpanded(null)}
              />
            ) : (
              <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
                <button className="btn btn-secondary" style={{ width: "auto" }} onClick={() => setExpanded(r.id)}>
                  Edit
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ width: "auto" }}
                  onClick={async () => {
                    await adminApi.patchRule(r.id, { markVerified: true });
                    await refresh();
                  }}
                >
                  Mark verified
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ width: "auto" }}
                  onClick={async () => {
                    await adminApi.patchRule(r.id, { status: r.status === "ACTIVE" ? "DISABLED" : "ACTIVE" });
                    await refresh();
                  }}
                >
                  {r.status === "ACTIVE" ? "Disable" : "Enable"}
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ width: "auto" }}
                  onClick={async () => {
                    await adminApi.duplicateRule(r.id);
                    await refresh();
                  }}
                >
                  Duplicate
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function effectColor(effect: string): string {
  switch (effect) {
    case "BLOCK":
      return "var(--color-danger-border)";
    case "MANUAL_CHECK":
      return "var(--color-warn-border)";
    case "RESTRICT":
      return "var(--color-warn-border)";
    case "WARN":
      return "var(--color-text-muted)";
    default:
      return "var(--color-accent)";
  }
}

function RuleForm({ rule, onSaved, onCancel }: { rule: RuleRow; onSaved: () => void; onCancel: () => void }) {
  const [userMessage, setUserMessage] = useState(rule.user_message ?? "");
  const [adminNotes, setAdminNotes] = useState(rule.admin_notes ?? "");
  const [carrier, setCarrier] = useState(rule.carrier ?? "");
  const [service, setService] = useState(rule.service ?? "");
  const [effect, setEffect] = useState(rule.effect);
  const [saving, setSaving] = useState(false);

  return (
    <div className="stack-sm">
      <div className="field">
        <label>Effect</label>
        <select value={effect} onChange={(e) => setEffect(e.target.value)}>
          {["ALLOW", "WARN", "RESTRICT", "BLOCK", "MANUAL_CHECK"].map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Carrier (blank = all)</label>
        <input value={carrier} onChange={(e) => setCarrier(e.target.value)} />
      </div>
      <div className="field">
        <label>Service (blank = all)</label>
        <input value={service} onChange={(e) => setService(e.target.value)} />
      </div>
      <div className="field">
        <label>Sender-facing message</label>
        <input value={userMessage} onChange={(e) => setUserMessage(e.target.value)} />
      </div>
      <div className="field">
        <label>Admin notes</label>
        <input value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
      </div>
      <div style={{ display: "flex", gap: "var(--space-2)" }}>
        <button
          className="btn btn-primary"
          style={{ width: "auto" }}
          disabled={saving}
          onClick={async () => {
            setSaving(true);
            await adminApi.patchRule(rule.id, {
              effect,
              carrier: carrier || null,
              service: service || null,
              userMessage: userMessage || null,
              adminNotes: adminNotes || null,
            });
            setSaving(false);
            onSaved();
          }}
        >
          Save
        </button>
        <button className="btn btn-ghost" style={{ width: "auto" }} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
